/**
 * Single event in the analytics payload.
 */
export interface AnalyticsEvent {
  event_name: string;
  properties: Record<string, unknown>;
}

/**
 * The payload structure expected by the recipient.
 * Events are wrapped in an array, even for single events.
 */
export interface AnalyticsEventPayload {
  events: AnalyticsEvent[];
}

export interface SuperProperties {
  checkout_id?: string;
  distinct_id?: string;
  transaction_id?: string;
}

const SUPER_PROPERTIES_STORAGE_KEY = 'analytics_super_properties';

/**
 * Extract super properties from URL parameters.
 * checkout_id: from analytics_checkout_id
 * distinct_id: from analytics_distinct_id
 * transaction_id: from analytics_transaction_id, falls back to pi
 */
export function extractSuperPropertiesFromUrl(): SuperProperties {
  if (typeof window === 'undefined' || !window.location) {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  return {
    checkout_id: params.get('analytics_checkout_id') || undefined,
    distinct_id: params.get('analytics_distinct_id') || undefined,
    transaction_id: params.get('analytics_transaction_id') || params.get('pi') || undefined,
  };
}

const isDev = import.meta.env.DEV;

/**
 * Safely extract a property value, logging and returning undefined on failure.
 * Never throws - always returns undefined if extraction fails.
 */
export function safeExtractProperty<T>(
  extractor: () => T,
  propertyName: string
): T | undefined {
  try {
    const value = extractor();
    if (value === null || value === undefined) {
      if (isDev) {
        console.warn(`[Analytics] Property "${propertyName}" is undefined`);
      }
      return undefined;
    }
    return value;
  } catch (error) {
    if (isDev) {
      console.warn(`[Analytics] Failed to extract "${propertyName}":`, error);
    }
    return undefined;
  }
}

/**
 * Filter out undefined and null properties from an object.
 * Returns a new object with only valid properties.
 */
export function filterValidProperties(
  props: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(props).filter(
      ([, value]) => value !== undefined && value !== null
    )
  );
}

/**
 * Store analytics super properties for later use (e.g., return page).
 * Never throws - failures are logged in development.
 */
export function storeSuperProperties(superProperties: SuperProperties): void {
  try {
    const payload = filterValidProperties(
      superProperties as Record<string, unknown>
    );
    sessionStorage.setItem(
      SUPER_PROPERTIES_STORAGE_KEY,
      JSON.stringify(payload)
    );
  } catch (error) {
    if (isDev) {
      console.warn('[Analytics] Failed to store super properties:', error);
    }
  }
}

/**
 * Retrieve stored analytics super properties from sessionStorage.
 * Never throws - returns empty object on failure.
 */
export function getStoredSuperProperties(): SuperProperties {
  try {
    const stored = sessionStorage.getItem(SUPER_PROPERTIES_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as SuperProperties;
    }
  } catch (error) {
    if (isDev) {
      console.warn('[Analytics] Failed to retrieve super properties:', error);
    }
  }
  return {};
}

/**
 * Safely track an analytics event, catching and logging any errors.
 * Never throws - always proceeds silently on failure.
 */
export function safeTrack(
  track: (eventName: string, properties?: Record<string, unknown>) => void,
  eventName: string,
  properties: Record<string, unknown> = {}
): void {
  try {
    track(eventName, filterValidProperties(properties));
  } catch (error) {
    if (isDev) {
      console.warn(`[Analytics] Tracking error for "${eventName}":`, error);
    }
  }
}

/**
 * Price-related properties for analytics events.
 */
export interface PriceProperties {
  sku?: string;
  currency?: string;
  local_price_before_tax?: number;
  tax?: number;
  local_price_after_tax?: number;
}

/**
 * Minimal session data structure for price extraction.
 */
interface SessionDataForPricing {
  price_data?: {
    total_price?: number;
    subtotal_price?: number;
  };
  order?: {
    sku?: string;
    currency?: string;
  };
}

/**
 * Safely extract price-related properties from session data.
 * Returns an object with only the properties that could be extracted.
 * Never throws - missing or invalid data results in undefined properties.
 */
export function extractPriceProperties(
  sessionData: SessionDataForPricing | null | undefined
): PriceProperties {
  const result: PriceProperties = {};

  if (!sessionData) {
    if (isDev) {
      console.warn('[Analytics] No session data for price extraction');
    }
    return result;
  }

  // Extract SKU
  result.sku = safeExtractProperty(
    () => sessionData.order?.sku,
    'sku'
  );

  // Extract currency
  result.currency = safeExtractProperty(
    () => sessionData.order?.currency,
    'currency'
  );

  // Extract subtotal (local_price_before_tax)
  result.local_price_before_tax = safeExtractProperty(
    () => sessionData.price_data?.subtotal_price,
    'local_price_before_tax'
  );

  // Extract total (local_price_after_tax)
  result.local_price_after_tax = safeExtractProperty(
    () => sessionData.price_data?.total_price,
    'local_price_after_tax'
  );

  // Calculate tax if both prices are available
  const subtotal = sessionData.price_data?.subtotal_price;
  const total = sessionData.price_data?.total_price;
  if (typeof subtotal === 'number' && typeof total === 'number') {
    result.tax = total - subtotal;
  } else if (isDev) {
    console.warn('[Analytics] Cannot calculate tax - missing price data');
  }

  return result;
}

/**
 * Build the analytics event payload in the format expected by the recipient.
 * Merges super properties and timestamp into the flat properties object.
 * Automatically filters out undefined/null properties.
 */
export function buildAnalyticsPayload(
  eventName: string,
  superProperties: SuperProperties,
  properties: Record<string, unknown> = {}
): AnalyticsEventPayload {
  const rawProperties = {
    checkout_id: superProperties.checkout_id,
    distinct_id: superProperties.distinct_id,
    transaction_id: superProperties.transaction_id,
    timestamp: Date.now(),
    ...properties,
  };

  const event: AnalyticsEvent = {
    event_name: eventName,
    properties: filterValidProperties(rawProperties),
  };

  return { events: [event] };
}

/**
 * Send an analytics event to the configured webhook URL.
 * Fire-and-forget - never throws, failures are logged in development.
 */
export async function sendAnalyticsEvent(
  webhookUrl: string | undefined,
  eventName: string,
  superProperties: SuperProperties,
  properties: Record<string, unknown> = {}
): Promise<void> {
  if (!webhookUrl) {
    if (isDev) {
      console.log(`[Analytics] Skipped (no URL): ${eventName}`, properties);
    }
    return;
  }

  const payload = buildAnalyticsPayload(eventName, superProperties, properties);

  if (isDev) {
    console.log(`[Analytics] Sending: ${eventName}`, payload);
  }

  fetch(webhookUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
    .then((response) => {
      if (isDev) {
        if (response.ok) {
          console.log(`[Analytics] ✓ ${eventName} sent successfully`);
        } else {
          console.warn(
            `[Analytics] ✗ ${eventName} failed with status ${response.status}`
          );
        }
      }
    })
    .catch((error) => {
      if (isDev) {
        console.warn(`[Analytics] ✗ ${eventName} failed:`, error);
      }
    });
}

export function createTracker(
  webhookUrl: string | undefined,
  superProperties: SuperProperties
) {
  return function track(
    eventName: string,
    properties: Record<string, unknown> = {}
  ): void {
    sendAnalyticsEvent(webhookUrl, eventName, superProperties, properties);
  };
}

/**
 * Map Stripe's camelCase express-payment-type names to our snake_case convention.
 */
const PAYMENT_METHOD_MAP: Record<string, string> = {
  applePay: 'apple_pay',
  googlePay: 'google_pay',
};

export function normalizePaymentMethod(raw: string): string {
  return PAYMENT_METHOD_MAP[raw] ?? raw;
}

/**
 * Checkout analytics data stored for use on success/failure pages.
 */
export interface CheckoutAnalyticsData extends PriceProperties {
  is_card_details_saved?: boolean;
  payment_method?: string;
}

const CHECKOUT_ANALYTICS_KEY = 'checkout_analytics_data';

/**
 * Store checkout analytics data in sessionStorage for use on success/failure pages.
 * Never throws - failures are logged in development.
 */
export function storeCheckoutAnalyticsData(data: CheckoutAnalyticsData): void {
  try {
    sessionStorage.setItem(CHECKOUT_ANALYTICS_KEY, JSON.stringify(filterValidProperties(data as Record<string, unknown>)));
  } catch (error) {
    if (isDev) {
      console.warn('[Analytics] Failed to store checkout analytics data:', error);
    }
  }
}

/**
 * Retrieve checkout analytics data from sessionStorage.
 * Never throws - returns empty object on failure.
 */
export function getCheckoutAnalyticsData(): CheckoutAnalyticsData {
  try {
    const stored = sessionStorage.getItem(CHECKOUT_ANALYTICS_KEY);
    if (stored) {
      return JSON.parse(stored) as CheckoutAnalyticsData;
    }
  } catch (error) {
    if (isDev) {
      console.warn('[Analytics] Failed to retrieve checkout analytics data:', error);
    }
  }
  return {};
}

/**
 * Clear checkout analytics data from sessionStorage.
 * Never throws - failures are logged in development.
 */
export function clearCheckoutAnalyticsData(): void {
  try {
    sessionStorage.removeItem(CHECKOUT_ANALYTICS_KEY);
  } catch (error) {
    if (isDev) {
      console.warn('[Analytics] Failed to clear checkout analytics data:', error);
    }
  }
}
